import { createAppKit } from '@reown/appkit'
import { sepolia, mainnet } from '@reown/appkit/networks'
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import { watchAccount, getChainId, disconnect, getAccount, reconnect, watchChainId } from '@wagmi/core'
import { _log } from '../helpers'

const projectId = process.env.VUE_APP_WALLETCONNECT_PROJECT_ID
const chains = [mainnet, sepolia]
const chainId = parseInt(process.env.VUE_APP_CHAIN_ID)

let web3modal, wagmiConfig

const wagmiSetup = () => {
  const wagmiAdapter = new WagmiAdapter({
    networks: chains,
    projectId,
  })

  web3modal = createAppKit({
    adapters: [wagmiAdapter],
    networks: chains,
    projectId,
    // Enable walletconnect and injected (MetaMask)
    enableInjectedProvider: true,
    enableWalletConnect: true
  })

  wagmiConfig = wagmiAdapter.wagmiConfig

  // Debug function to check wallet states
  const debugWalletState = (store) => {
    _log('=== Wallet State Debug ===')

    // Check wagmi account
    const account = getAccount(wagmiConfig)
    _log('Wagmi Account:', account)

    // Check chain ID
    try {
      const currentChainId = getChainId(wagmiConfig)
      _log('Current Chain ID:', currentChainId)
    } catch (error) {
      _log('Chain ID Error:', error)
    }

    // Check MetaMask
    if (window.ethereum) {
      window.ethereum.request({ method: 'eth_accounts' })
        .then(async accounts => {
          _log('MetaMask Accounts:', accounts)
          if(accounts?.length > 0){
            await reconnect(wagmiConfig)
            const account = getAccount(wagmiConfig)
            store.dispatch('auth/setAccount', account)
          }
          return window.ethereum.request({ method: 'eth_chainId' })
        })
        .then(chainId => {
          _log('MetaMask Chain ID:', chainId)
        })
        .catch(error => {
          _log('MetaMask Error:', error)
        })
    }

    // Check AppKit state
    _log('AppKit State:', web3modal.getState())

    // Subscribe to AppKit state changes
    const unsubscribeState = web3modal.subscribeState((state) => {
      _log('AppKit state changed:', state)
    })

    return {
      cleanup: () => unsubscribeState
    }
  }

  const getCurrentChainId = () => {
    try {
      return getChainId(wagmiConfig)
    } catch (error) {
      _log('Failed to get chain ID:', error)
      return null
    }
  }


  return {
    web3modal,
    chainId,
    getCurrentChainId,
    wagmiConfig,
    debugWalletState,
    forceReconnect,
    syncWithMetaMask
  }
}

const forceReconnect = async () => {
  try {
    _log('Attempting to reconnect...')
    await reconnect(wagmiConfig)
    _log('Reconnection successful')
  } catch (error) {
    _log('Reconnection failed:', error)
  }
}

// Sync MetaMask with AppKit
// Called if mismatch detected and when clicking connect button
export const syncWithMetaMask = async () => {
  if (window.ethereum && window.ethereum.isMetaMask) {
    _log('has metamask')
    try {
      // Request accounts to ensure MetaMask is properly connected
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      })

      if (accounts.length > 0) {
        _log('MetaMask connected with accounts:', accounts)
        // Force wagmi to recognize the connection
        await forceReconnect()
      }
    } catch (error) {
      _log('Failed to sync with MetaMask:', error)
    }
  }
}

const walletEventSetup = (store) => {
  const unsubscribeAccount = watchAccount(wagmiConfig, {
    onChange(account, prevAccount) {

      _log('Account changed:', {
        address: account.address,
        chainId: account.chainId,
        isConnected: account.isConnected,
        prevAddress: prevAccount?.address,
        prevChainId: prevAccount?.chainId
      })

      store.dispatch('auth/setAccount', account)


      if(account.isConnected){
        if (account.chainId !== prevAccount?.chainId) {
          _log('Chain changed:', {
            from: prevAccount?.chainId,
            to: account.chainId
          })

          store.dispatch('ui/checkShowChain', account.chainId)
        }

        if (account.address !== prevAccount?.address) {
          _log('Address changed:', {
            from: prevAccount?.address,
            to: account.address
          })
        }

      }else{
        _log('Account disconnected')
      }
    }
  })

  // Watch chain/network changes using Wagmi's built-in watcher
  const unsubscribeChain = watchChainId(wagmiConfig, {
    onChange(chainId, prevChainId) {
      _log('Chain changed from Wagmi:', { from: prevChainId, to: chainId })
      store.dispatch('ui/checkShowChain', chainId)
    }
  })

  let cleanupMetaMask = null

  // Listen to MetaMask events directly
  if (window.ethereum) {
    const handleAccountsChanged = (accounts) => {
      _log('MetaMask accountsChanged event fired')
      _log('New accounts:', accounts)
      _log('Wagmi will sync shortly via watchAccount...')
    }

    const handleChainChanged = (chainId) => {
      const chainIdDecimal = parseInt(chainId, 16)
      _log('MetaMask chain changed (decimal):', chainIdDecimal)
      _log('Waiting for Wagmi to sync...')
    }

    window.ethereum.on('accountsChanged', handleAccountsChanged)
    window.ethereum.on('chainChanged', handleChainChanged)

    // Cleanup function
    cleanupMetaMask = () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged)
      window.ethereum.removeListener('chainChanged', handleChainChanged)
    }
  }

  return {
    cleanup: () => {
      unsubscribeAccount?.()
      unsubscribeChain?.()
      unsubscribeState?.()
      cleanupMetaMask?.()  
    }
  }
}

// Utility function to fix connection issues
// Nuclear option to fix persistent issues
const fixConnectionIssues = async (web3modal, wagmiConfig) => {
  try {

    await disconnect(wagmiConfig)

    localStorage.removeItem('wagmi.store')
    localStorage.removeItem('wagmi.cache')

    // 3. If MetaMask is available, request fresh connection
    if (window.ethereum && window.ethereum.isMetaMask) {
      await window.ethereum.request({
        method: 'wallet_requestPermissions',
        params: [{ eth_accounts: {} }]
      })
    }

    // 4. Open the modal for fresh connection
    web3modal.open()

  } catch (error) {
    _log('Error fixing connection:', error)
  }
}

export {
  wagmiSetup,
  walletEventSetup,
  fixConnectionIssues
}